
import { User, Session } from '@/types/auth';

// Simple local storage keys
export const AUTH_TOKEN_KEY = 'auth_token';
export const AUTH_USER_KEY = 'auth_user';
export const AUTH_SESSION_KEY = 'auth_session';

/**
 * Save auth data to localStorage and cookies
 */
export const saveAuthData = (token: string, user: User): void => {
  try {
    // Store in localStorage
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    
    // Store in cookies with 30 day expiration for longer persistence
    setCookie(AUTH_TOKEN_KEY, token, 30);
    setCookie(AUTH_USER_KEY, JSON.stringify(user), 30);
    
    console.log('Auth data saved to storage and cookies');
  } catch (error) {
    console.error('Failed to save auth data:', error);
  }
};

/**
 * Save the whole session object for better persistence
 */
export const saveSessionData = (session: Session): void => {
  try {
    if (!session) return;
    
    // Store session in localStorage
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    
    // Store in cookies with 30 day expiration
    setCookie(AUTH_SESSION_KEY, JSON.stringify(session), 30);
    
    console.log('Session data saved to storage and cookies');
  } catch (error) {
    console.error('Failed to save session data:', error);
  }
};

/**
 * Load auth data from localStorage or cookies
 */
export const loadAuthData = (): { token: string | null, user: User | null, session: Session | null } => {
  try {
    // Try localStorage first
    let storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    let storedUser = localStorage.getItem(AUTH_USER_KEY);
    let storedSession = localStorage.getItem(AUTH_SESSION_KEY);
    
    // If not in localStorage, try cookies
    if (!storedToken || !storedUser) {
      storedToken = getCookie(AUTH_TOKEN_KEY);
      storedUser = getCookie(AUTH_USER_KEY);
    }
    
    if (!storedSession) {
      storedSession = getCookie(AUTH_SESSION_KEY);
    }
    
    let parsedUser = null;
    let parsedSession = null;
    
    if (storedUser) {
      try {
        parsedUser = JSON.parse(storedUser);
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
    
    if (storedSession) {
      try {
        parsedSession = JSON.parse(storedSession);
      } catch (error) {
        console.error('Failed to parse session data:', error);
      }
    }
    
    if (parsedUser || storedToken || parsedSession) {
      console.log('Auth data loaded from storage or cookies');
      return {
        token: storedToken,
        user: parsedUser,
        session: parsedSession
      };
    }
  } catch (error) {
    console.error('Failed to load auth data:', error);
  }
  
  return {
    token: null,
    user: null,
    session: null
  };
};

/**
 * Clear auth data from localStorage and cookies
 */
export const clearAuthData = (): void => {
  try {
    // Clear from localStorage
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_SESSION_KEY);
    
    // Clear cookies
    deleteCookie(AUTH_TOKEN_KEY);
    deleteCookie(AUTH_USER_KEY);
    deleteCookie(AUTH_SESSION_KEY);
    
    console.log('Auth data cleared from storage and cookies');
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
};

/**
 * Helper function to set a cookie with secure flags when possible
 */
function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "; expires=" + date.toUTCString();
  
  // Add secure flags in production environment
  const isSecure = window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';
  const sameSiteValue = isSecure ? 'None' : 'Lax';
  
  document.cookie = name + "=" + encodeURIComponent(value) + expires + 
                   "; path=/; SameSite=" + sameSiteValue + secureFlag;
}

/**
 * Helper function to get a cookie
 */
function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Helper function to delete a cookie
 */
function deleteCookie(name: string): void {
  const isSecure = window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';
  const sameSiteValue = isSecure ? 'None' : 'Lax';
  
  document.cookie = name + '=; Path=/; SameSite=' + sameSiteValue + 
                   '; Expires=Thu, 01 Jan 1970 00:00:01 GMT' + secureFlag;
}
